/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Chartist from 'vendors/chartist/chartist';
import Component from 'can/component/component';
import template from './round.stache';
import DomEventsManager from 'scripts/dom-events-manager';
import can from 'can';
import appContext from 'scripts/app-context';
import dtm from 'scripts/services/dtm';

const pageType = window.globalSettings.pageType;

Component.extend({
    tag: 'recap-round',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            player: {
                Type: can.Map,
            },
            data: {
                Type: can.List,
                value: can.List,
                set(data) {
                    if (data) {
                        data.forEach((item, key) => {
                            const holeNum = +key + 1;
                            let holeId = holeNum;
                            const video = item.attr('video');

                            if (this.attr('startHole') == 10) {
                                if (holeId < 10) {
                                    holeId += 9;
                                } else {
                                    holeId = 18 - holeId;
                                }
                            }

                            if (video) {
                                video.attr('percentage', (holeNum / 18) * 100);
                                video.attr('hole', holeId);
                            }
                        });
                    }
                    return data;
                },
            },
            minValue: {
                type: 'number',
                value: 0,
            },
            maxValue: {
                type: 'number',
                value: 0,
            },
            startHole: {
                type: 'number',
                value: 1,
            },
            holes: {
                get() {
                    const startHole = this.attr('startHole');
                    let holes = [];

                    for (let i = startHole; i <= 18; i++) {
                        holes.push(i);
                    }

                    const additionalHoles = 18 - holes.length;
                    for (let i = 1; i <= additionalHoles; i++) {
                        holes.push(i);
                    }
                    return holes;
                },
            },
            zeroValue: {
                type: 'number',
                value: 0,
            },
            activeVideo: {
                Type: can.Map,
                value: null,
            },
            chartData: {
                get() {
                    if (this.attr('data')) {
                        const context = {
                            min: parseInt(this.attr('minValue') || 0),
                            max: parseInt(this.attr('maxValue') || 0),
                            previousValue: null,
                        };
                        let chartData = this.attr('data')
                            .serialize()
                            .map((item, index) => {
                                return this.normalizeValue(item.value, context);
                            });

                        if (chartData.length > 0) {
                            if (chartData.length < 18) {
                                let length = chartData.length;
                                let augmentationStartIndex = 18 - length;
                                chartData = chartData.concat(
                                    new Array(augmentationStartIndex),
                                );
                                _.fill(chartData, null, length);
                            }
                            context.previousValue = null;
                            chartData = [
                                this.normalizeValue(
                                    this.attr('zeroValue'),
                                    context,
                                ),
                                ...chartData,
                            ];
                            this.attr('noData', false);
                        } else {
                            this.attr('noData', true);
                        }
                        return chartData;
                    } else {
                        this.attr('noData', true);
                    }

                    return [];
                },
            },
        },
        normalizeValue(val, context) {
            if (!context) {
                return 0;
            }

            if (val === 0) {
                if (context.previousValue) {
                    return context.previousValue > 0 ? -0.05 : 0.05;
                } else {
                    return val;
                }
            } else if (val > 0) {
                context.previousValue = val;
                return Math.round(context.max * val) * -1;
            } else {
                context.previousValue = val;
                return Math.round(context.min * val);
            }
        },
        isActive(video) {
            const activeVideo = this.attr('activeVideo');
            if (activeVideo) {
                return activeVideo.identifier === video.identifier;
            }
            return false;
        },
        analyticsVideoSource() {
            switch (pageType) {
                case 'player-detail':
                    return 'Player Detail Recap';
                case 'scoring':
                    return 'Leader Board Recap';
                default:
                    return '';
            }
        },
        play(video) {
            let round = this.round;
            let player = this.player;
            let title =
                video.title ||
                `${player.firstName} ${player.lastName}: Hole ${
                    video.hole
                }, Round ${round}`;

            // set current round & title
            video.attr('title', title);
            video.attr('round', round);

            this.attr('activeVideo', video);
            dtm.trackVideoLaunch(video.title, this.analyticsVideoSource());
        },
    },
    events: {
        inserted() {
            DomEventsManager.subscribe(this);

            appContext.model.bind('drawer-content-tabs-toggle', () => {
                if (this.element) {
                    this.renderCharts();
                }
            });

            this.viewModel.bind('chartData', (ev, values) => {
                this.renderCharts();
            });

            this.renderCharts();
        },

        removed: function() {
            DomEventsManager.unsubscribe(this);
        },

        onLayout: function(layout) {
            if (this.layout && this.layout !== layout) {
                this.renderCharts();
            }
            this.layout = layout;
        },

        renderCharts() {
            if (!this.element) return;

            const min = parseInt(this.viewModel.attr('maxValue') || 0) * -1;
            const max = parseInt(this.viewModel.attr('minValue') || 0) * -1;

            const chartData = this.viewModel.attr('chartData');

            if (
                _.isEqual(chartData, this.chartData) &&
                this.max === max &&
                this.min === min
            ) {
                return;
            }
            this.chartData = chartData;
            this.max = max;
            this.min = min;

            this.drawChart(
                this.element.find('.positive-chart .chart-area'),
                this.chartData,
                0,
                this.max,
            );
            this.drawChart(
                this.element.find('.negative-chart .chart-area'),
                this.chartData,
                this.min,
                0,
            );
        },

        drawChart($el, chartData, min, max) {
            let height = $el.height();
            new Chartist.Line(
                $el,
                {
                    series: [
                        {
                            name: 'chart-area',
                            data: chartData,
                        },
                    ],
                },
                {
                    chartPadding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                    },
                    fullWidth: true,
                    showArea: true,
                    showPoint: false,
                    height: height + 'px',
                    showLine: true,
                    high: max,
                    low: min,
                    axisY: {
                        showLabel: false,
                        offset: 0,
                        showGrid: false,
                        onlyInteger: true,
                        scaleMinSpace: 1,
                    },
                    axisX: {
                        showGrid: false,
                        showLabel: false,
                        offset: 0,
                    },
                    lineSmooth: Chartist.Interpolation.simple({
                        divisor: 2,
                    }),
                },
            );
        },
    },
});
